import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface MarketingOffer {
  id: number;
  name: string;
  description: string | null;
  package_code: string | null;
  offer_type: 'FREE_GRANT' | 'STRIPE_DISCOUNT';
  access_duration_days: number | null;
  discount_id: number | null;
  billing_cycle: 'weekly' | 'monthly' | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  package: { name: string; package_code: string; is_private: boolean } | null;
  discount: { id: number; name: string; discount_code: string } | null;
  _count: { campaigns: number };
}

export interface MarketingOfferDraft {
  name?: string;
  description?: string | null;
  package_code?: string | null;
  offer_type?: string;
  access_duration_days?: number | null;
  discount_id?: number | null;
  billing_cycle?: string | null;
  is_active?: boolean;
}

function useInvalidateOffers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['marketing-offers'] });
}

export function useMarketingOffers() {
  return useQuery({
    queryKey: ['marketing-offers'],
    queryFn: async () => (await apiClient<{ offers: MarketingOffer[] }>('/marketing/offers')).offers,
  });
}

export function useCreateMarketingOffer() {
  const invalidate = useInvalidateOffers();
  return useMutation({
    mutationFn: async (draft: MarketingOfferDraft) =>
      (await apiClient<{ offer: MarketingOffer }>('/marketing/offers', { method: 'POST', body: JSON.stringify(draft) })).offer,
    onSuccess: invalidate,
  });
}

export function useUpdateMarketingOffer() {
  const invalidate = useInvalidateOffers();
  return useMutation({
    mutationFn: async ({ id, ...fields }: MarketingOfferDraft & { id: number }) =>
      (await apiClient<{ offer: MarketingOffer }>(`/marketing/offers/${id}`, { method: 'PUT', body: JSON.stringify(fields) })).offer,
    onSuccess: invalidate,
  });
}

export function useDeleteMarketingOffer() {
  const invalidate = useInvalidateOffers();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ success: boolean }>(`/marketing/offers/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
